import { Button, Group } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useSearchInvoices } from 'api';
import type { SearchInvoicesParams } from 'api/models';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { treatClient } from 'shared/serializer';
import { getSearchInvoicesUrlParams } from 'shared/utils/hooks';
import { clientActions } from 'store/client/client.actions';

import { useInvoiceFormContext } from '../InvoiceForm.context';
import type {
  CustomProductLine,
  InvoiceFormFieldValues,
  InvoiceFormSharedProps,
} from '../InvoiceForm.types';
import { getProductLine } from '../InvoiceForm.utils';

import InvoiceProductLine from './InvoiceProductLine/InvoiceProductLine';
import InvoiceProductLineInvoice from './InvoiceProductLine/InvoiceProductLineInvoice';

import styleIdentifiers from './invoiceLines.module.scss';

const styles = classNames.bind(styleIdentifiers);

type InvoiceLinesProps = InvoiceFormSharedProps & {
  initialValues?: Partial<InvoiceFormFieldValues>;
};

export default function InvoiceLines(props: InvoiceLinesProps) {
  const { noTva, currency, isCreditNote } = props;
  const { t } = useTranslation();
  const form = useInvoiceFormContext();

  const location = useLocation();
  const paramsQuery = getSearchInvoicesUrlParams(location);
  const _debtor = form.values.debtor_attributes;
  const productLines = form.values.product_lines_attributes;
  const { setFieldValue, insertListItem, removeListItem } = form;

  const [params, setParams] = useState<SearchInvoicesParams>({
    currency,
  });

  const { data: invoicesResponse } = useSearchInvoices(params);

  const addProduct = useCallback(
    (data: Partial<CustomProductLine> = {}) =>
      insertListItem(
        'product_lines_attributes',
        getProductLine(noTva, { isInvoice: false, ...data }) as CustomProductLine,
      ),
    [insertListItem, noTva],
  );

  const addInvoice = useCallback(
    (data: Partial<CustomProductLine> = {}) =>
      insertListItem(
        'product_lines_attributes',
        getProductLine(noTva, { isInvoice: true, ...data }) as CustomProductLine,
      ),
    [insertListItem, noTva],
  );

  const removeLine = useCallback(
    (index: number) => removeListItem('product_lines_attributes', index),
    [removeListItem],
  );

  useLayoutEffect(() => {
    if (Object.keys(paramsQuery).length) {
      setParams(currentParams => ({
        ...currentParams,
        ...paramsQuery,
        reference: (paramsQuery?.reference || [])[0],
      }));
    }
  }, []);

  useEffect(() => {
    if (!productLines?.length && !isCreditNote) {
      addProduct();
    }
  }, [productLines?.length, isCreditNote, addProduct]);

  useEffect(() => {
    if (invoicesResponse) {
      const invoices = invoicesResponse.data.filter(e =>
        (paramsQuery?.reference || []).includes(e.attributes.reference),
      );

      const debtorId = invoices.length ? invoices[0].attributes?.debtor?.id : undefined;

      if (debtorId) {
        clientActions.detail({
          id: Number(debtorId),
          callback: ({ data: dataDebtor }: any) => {
            const findedDebtor = treatClient(dataDebtor);

            if (!findedDebtor) {
              return;
            }

            setFieldValue('debtor_attributes', findedDebtor);
          },
        });
      }

      invoices.forEach(e => {
        if (e.attributes?.debtor?.id === debtorId) {
          addInvoice({
            linked_invoice_id: Number(e.id),
            total_htva: -e.attributes.total_htva,
            total_tvac: -e.attributes.total_tvac,
            product_attributes: {
              name: e.attributes.reference,
              tax_category_code: '00',
              current_unity_price_htva: -e.attributes.total_htva,
            },
          });
        }
      });
    }
  }, [invoicesResponse]);

  // The "force tax_category_code='00' when debtor is noTva" sync used to live
  // here as a useEffect, but Mantine's controlled mode plus unstable callback
  // refs turned it into a render→setState loop on CI. The handler now lives
  // in InvoiceForm.tsx's handleSelectDebtor (the only event that switches the
  // debtor's noTva state), and getProductLine(noTva, ...) covers newly added
  // lines.

  return (
    <div className={styles('InvoiceLines')}>
      <table>
        <thead>
          <tr>
            <td className={styles('name')}>{t(i18nKeys.INVOICE.PRODUCT)}</td>
            <td>{t(i18nKeys.INVOICE.QTE)}</td>
            <td className={styles('price')}>{t(i18nKeys.PRICE)}</td>
            <td>{t(i18nKeys.INVOICING.CREATE.DISCOUNT_PERCENTAGE)}</td>
            <td className={styles('amount')}>{t(i18nKeys.TOTAL_NOT_VAT_PRICE)}</td>
            <td>{t(i18nKeys.INVOICING.CREATE.VAT_PERCENTAGE)}</td>
            <td className={styles('amount')}>{t(i18nKeys.TOTAL_VAT_PRICE)}</td>
            <td style={{ minWidth: 32 }} />
          </tr>
        </thead>
        <tbody>
          {(productLines ?? []).map((line, index) =>
            line.isInvoice ? (
              <InvoiceProductLineInvoice
                {...props}
                key={index}
                lineIndex={index}
                remove={removeLine}
                selected={(productLines ?? [])
                  .filter(e => e.isInvoice && e.linked_invoice_id)
                  .map(e => Number(e.linked_invoice_id))}
              />
            ) : (
              <InvoiceProductLine
                {...props}
                key={index}
                removeProductLine={removeLine}
                lineIndex={index}
              />
            ),
          )}
        </tbody>
      </table>
      <Group gap="xs" mt="md" justify="flex-end">
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} stroke={1.5} />}
          onClick={() => addProduct()}
        >
          {t(i18nKeys.INVOICING.CREATE.ADD_PRODUCT)}
        </Button>
        {isCreditNote && (
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} stroke={1.5} />}
            onClick={() =>
              addInvoice({
                product_attributes: {
                  name: '',
                  tax_category_code: '00',
                  current_unity_price_htva: 0,
                },
              })
            }
          >
            {t(i18nKeys.ADD_INVOICE)}
          </Button>
        )}
      </Group>
    </div>
  );
}
